import type { Aabb, Vec2 } from "@townsy/contracts";

/**
 * Coordinate conventions shared by the server simulation and the client renderer.
 *
 *  - World units are metres. y is up. Movement happens on the x/z ground plane.
 *  - x grows east, z grows north. The main street runs east-west along z = 0.
 *  - A yaw of 0 faces north (+z); +PI/2 faces east (+x); PI faces south (-z); -PI/2 faces west.
 *    forward(yaw) = (sin(yaw), cos(yaw)).
 */
export const YAW_NORTH = 0;
export const YAW_EAST = Math.PI / 2;
export const YAW_SOUTH = Math.PI;
export const YAW_WEST = -Math.PI / 2;

export function forward(yaw: number): Vec2 {
  return { x: Math.sin(yaw), z: Math.cos(yaw) };
}

export function yawFromDirection(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

/** Normalize an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r <= -Math.PI) r += Math.PI * 2;
  return r;
}

export function aabb(minX: number, minZ: number, maxX: number, maxZ: number): Aabb {
  return { minX, minZ, maxX, maxZ };
}

/** Axis-aligned box from a center point and full width/depth. */
export function aabbAt(cx: number, cz: number, width: number, depth: number): Aabb {
  return { minX: cx - width / 2, minZ: cz - depth / 2, maxX: cx + width / 2, maxZ: cz + depth / 2 };
}

export function aabbCenter(b: Aabb): Vec2 {
  return { x: (b.minX + b.maxX) / 2, z: (b.minZ + b.maxZ) / 2 };
}

export function aabbSize(b: Aabb): { width: number; depth: number } {
  return { width: b.maxX - b.minX, depth: b.maxZ - b.minZ };
}

export function aabbContains(b: Aabb, x: number, z: number): boolean {
  return x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ;
}

export function aabbOverlaps(a: Aabb, b: Aabb): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minZ < b.maxZ && a.maxZ > b.minZ;
}

/** Grow a box by `r` on every side (used to test a circle against a box). */
export function aabbInflate(b: Aabb, r: number): Aabb {
  return { minX: b.minX - r, minZ: b.minZ - r, maxX: b.maxX + r, maxZ: b.maxZ + r };
}

export function distance(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.hypot(dx, dz);
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
